import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { fnGetWhLocLotCtPltInv, fnGetWhSnInv } from "@shared/utils/InvUtils";
import {
  getInitFormData,
  applyAutoSlip,
  CalAm,
  CalAmBulk,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, globalMenu } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MTI_REG_002_POP_001 from "@menu/MTL/SN_MTL_MTI_REG_002/SN_MTL_MTI_REG_002_POP_001";
import SN_MTL_MTI_REG_002_POP_002 from "@menu/MTL/SN_MTL_MTI_REG_002/SN_MTL_MTI_REG_002_POP_002";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import * as converter from "@shared/utils/ValueConverterUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";
import ExcelUpload_S01_Validate from "@menu/MTL/SN_MTL_MTR_REG_002/ExcelUpload_S01_Validate";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MTI_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);

  const sheetref1 = useRef<any>(null); // excel download 용
  //#endregion

  // 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
    sheet1: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns.grid1.length, columns.grid2.length, Menuprops]); // 26.09.21 kdy 도움창 교체로 컬럼이 바뀔 때 재조회되지 않게 길이만 감시

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "6", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelUpload,
    onExcelDownLoad,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mti_reg_002_01",
        params: searchDataToUse,
      };

      setLotSnData([]);

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
        sheet1: [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (
        focusedRowNodeH?.data.tp_trans == "008" &&
        converter.getString(focusedRowNodeH.data.no_io).endsWith("_T")
      ) {
        showDialog("alter", "해당건은 이 화면에서 추가할 수 없습니다.");
        return false;
      }

      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (
      focusedRowNodeH.data.tp_trans == "008" &&
      converter.getString(focusedRowNodeH.data.no_io).endsWith("_T")
    ) {
      await showDialog("alter", "해당건은 이 화면에서 삭제할 수 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    // 삭제할 헤더의 no_io로 출고정보를 필터한다.
    getGridCurrent("grid4").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (
      focusedRowNodeH.data.tp_trans == "008" &&
      converter.getString(focusedRowNodeH.data.no_io).endsWith("_T")
    ) {
      showDialog("alter", "해당건은 이 화면에서 삭제할 수 없습니다.");
      return false;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
    const isInsertRow = insertRows.some(
      (row: any) =>
        row.no_line === focusedRowNodeL.data.no_line &&
        row.no_io === focusedRowNodeL.data.no_io
    );

    if (isInsertRow) {
      resetRowData();
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
      });
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtiolotsnChanges = getGridCurrent("grid4").getChange();

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 7,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid4"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtiolotsnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_lotsn: qtiolotsnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MTI_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();

      // 중계서버 연동키(no_io_if_key)를 재조회 없이 해당 행에 반영
      if (Array.isArray(ok)) {
        ok.forEach((item: any) => {
          getGridCurrent("grid1").forEachNode((node: RowNode) => {
            if (node.data.no_io === item.no_io) {
              node.updateData({
                ...node.data,
                no_io_if_key: item.no_io_if_key,
              });
            }
          });
        });
      }

      setLotSnData([]);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region onExcelUpload
  const onExcelUpload = async () => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = 0;
    getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
      if (node.data.state === "insert") {
        focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
      }
    });

    getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

    const excel = (
      <ExcelUpload_S01_Validate Menuprops={Menuprops} grouping={[]} />
    );

    const result = await showResizablePopupModal({
      title: Menuprops.nm_menu + " 엑셀 업로드",
      children: excel,
      okayBtnText: "확인",
      validation: true,
    });
    if (!result || result == "close") return;

    const line = result[0];

    if (line.length > 0) {
      line.forEach((item: any) => {
        const addedNode = onAddRow();

        if (addedNode) {
          for (const key in item) {
            addedNode.setDataValue(key, item[key], "noEvent");
          }

          const qtGood = Number(item.qt_good) || 0;
          const qtBad = Number(item.qt_bad) || 0;
          const umK = Number(item.um_k) || 0;
          const amK = Number(item.am_k) || 0;

          addedNode.setDataValue("qt_good", qtGood, "noEvent");
          addedNode.setDataValue("qt_bad", qtBad, "noEvent");
          addedNode.setDataValue("qt", qtGood + qtBad, "noEvent");
          addedNode.setDataValue("um_k", umK, "noEvent");
          addedNode.setDataValue("am_k", amK, "noEvent");
        }
      });
    }
  };
  //#endregion

  //#region onExcelDownLoad
  const onExcelDownLoad = async () => {
    try {
      const columnKeys = sheetref1.current
        ?.getAllDisplayedColumns()
        .filter((col: any) => {
          const cId = col.getColId();
          return cId !== "fixIndicator";
        })
        .map((c: any) => c.getColId());

      sheetref1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        fg_trns: "1",
        tp_trans: "top1",
        rt_exch: 1,
        cd_exch: "000",
        tp_um: "001",
        tp_slip: "6",
        fg_slip: "2",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        no_hst: 0,
        no_io: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  // 26.09.21 kdy 대체유형(tp_trans)이 008이면 하단 cd_wh 도움창을 전체창고(POPUP_WH_ALL)로, 아니면 원래 도움창으로 교체
  const applyWhPopupByTpTrans = (tp_trans: any) => {
    const nextPopup =
      converter.getString(tp_trans) === "008" ? "POPUP_WH_ALL" : "POPUP_WH";
    const colWh = columns.grid2.find((c: any) => c.cd_column === "cd_wh");
    if (!colWh || colWh.cd_popup === nextPopup) return; // 같은 도움창이면 컬럼 재생성 안 함

    setColums((prev: any) => ({
      ...prev,
      grid2: prev.grid2.map((c: any) =>
        c.cd_column === "cd_wh" ? { ...c, cd_popup: nextPopup } : c
      ),
    }));
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });

          getGridCurrent("grid4").forEachNode((node: RowNode) => {
            if (node.data.no_io === rowData.no_io) {
              node.setDataValue(colId, rowData.cd_io, "noEvent");
              node.setDataValue("fg_io", ioType.fg_io, "noEvent");
              node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            }
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io, "noEvent");
        });
        getGridCurrent("grid4").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue(colId, rowData.dt_io, "noEvent");
          }
        });
        break;
      case "cd_pjt":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_pjt, "noEvent");
        });
        break;
      case "tp_trans":
        applyWhPopupByTpTrans(rowData.tp_trans);
        // 26.09.21 kdy 008(전체창고)에서 다른 유형으로 바뀌면 공정창고가 남을 수 있어 하단 창고를 전부 비운다
        if (
          converter.getString(event.oldValue) === "008" &&
          converter.getString(rowData.tp_trans) !== "008" &&
          rowCount > 0
        ) {
          // 창고가 무효가 되면 그 창고에서 적용한 LOT/SN 재고도 무효라 해당 전표의 LOT/SN 행을 먼저 지운다.
          // 행삭제(onDeleteRow)와 같은 기준: 신규 라인의 적용분은 재고 그리드(grid3)로 되돌리고, 저장된 라인은 삭제만 한다.
          const insertRowsL = getGridCurrent("grid2").getChange().insert ?? [];
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            getGridCurrent("grid4").applyFilters({
              no_io: node.data.no_io,
              no_line: node.data.no_line,
            });
            const isInsertRow = insertRowsL.some(
              (row: any) =>
                row.no_line === node.data.no_line &&
                row.no_io === node.data.no_io
            );
            if (isInsertRow) {
              resetRowData();
            } else {
              getGridCurrent("grid4").deleteAllRows();
            }
          });
          getGridCurrent("grid3").applyFilters({ cd_item: "", cd_wh: "" });

          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue("qt_good", 0, "noEvent");
            node.setDataValue("qt_bad", 0, "noEvent");
            node.setDataValue("qt", 0, "noEvent");
            node.setDataValue("nm_wh", "", "noEvent");
            node.setDataValue("cd_wh", "");
          });
          CalAm(
            gridRefs.current["grid1"],
            gridRefs.current["grid2"],
            "rt_exch"
          );
        }
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      applyWhPopupByTpTrans(selectedRow.tp_trans);
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line: "",
        });
      }
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    if (id === "btnApplyInv") {
      // 상단 grid 포커스 행 기준: 신규행이면 신규 건, 기존행이면 해당 건에 추가.
      const focusedH = getGridCurrent("grid1").focusedRowNode();
      if (!focusedH) {
        await showDialog(
          "alter",
          "상단에서 출고 건을 선택하거나 신규행을 추가해 주세요."
        );
        return;
      }
      // 기존 건에 추가 시 대체유형이 없으면 저장이 막히므로 먼저 선택하도록 안내
      if (
        focusedH.data.state !== "insert" &&
        (focusedH.data.tp_trans == null || focusedH.data.tp_trans === "")
      ) {
        await showDialog("alter", "대체유형을 먼저 선택해 주세요.");
        return;
      }
    } else {
      // 신규행이 없으면 Dialog로 띄우기
      const qtiohChanges = getGridCurrent("grid1").getChange().insert;
      if (qtiohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }
    }

    switch (id) {
      case "btn_POP1":
        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_MTI_REG_002_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "출고요청적용",
          children: component,
          validation: true,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        // 요청도움창 Header 결과 적용
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            focusedRowNodeH.setDataValue(key, headerResult[0][key]);
          }
        }

        const lotItemList: any = [];
        const snItemList: any = [];

        // 요청도움창 Line 결과 적용
        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                switch (key) {
                  case "no_line":
                    addedNode.setDataValue(
                      "no_line_req",
                      result.no_line,
                      "noEvent"
                    );
                    break;
                  default:
                    addedNode.setDataValue(key, result[key], "noEvent");
                    break;
                }
              }
              addedNode.setDataValue("yn_req", "Y", "noEvent");
              if (result.fg_unit === "QT") {
                addedNode.setDataValue(
                  "qt_good",
                  result.qt - result.qt_io,
                  "noEvent"
                );
                addedNode.setDataValue(
                  "qt",
                  result.qt - result.qt_io,
                  "noEvent"
                );
              } else {
                addedNode.setDataValue("qt", 0, "noEvent");
              }
              addedNode.setDataValue(
                "qt_req",
                result.qt - result.qt_io,
                "noEvent"
              );
              addedNode.setDataValue("um_k", result.um, "noEvent");

              switch (result.fg_unit) {
                case "LOT":
                  lotItemList.push({
                    cd_biz: focusedRowNodeH.data.cd_biz,
                    cd_item: result.cd_item,
                    cd_wh: result.cd_wh,
                    cd_loc: "",
                  });
                  break;
                case "SN":
                  snItemList.push({
                    cd_biz: focusedRowNodeH.data.cd_biz,
                    cd_item: result.cd_item,
                    cd_wh: result.cd_wh,
                  });
                  break;
              }

              getGridCurrent("grid2").applyBlock(addedNode);
            }
          }
        });
        CalAmBulk(
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          "rt_exch"
        );
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);

        if (lotItemList.length > 0) {
          const searchLotData = await fnGetWhLocLotCtPltInv(lotItemList);
          const lotData = searchLotData.map((item: any) => {
            const { no_lot, ...rest } = item; // no_lot만 추출
            return {
              ...rest,
              no_barcode: no_lot, // 필드명 바꾸기
              fg_unit: "LOT", // LOT/SN구분
              qt_isu: 0, // 적용량 추가
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = lotData.filter(
              (newItem: any) =>
                !prevData.some(
                  (oldItem: any) =>
                    oldItem.cd_item === newItem.cd_item &&
                    oldItem.cd_wh === newItem.cd_wh
                )
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        if (snItemList.length > 0) {
          const searchSnData = await fnGetWhSnInv(snItemList);
          const snData = searchSnData.map((item: any) => {
            const { no_sn, ...rest } = item;
            return {
              ...rest,
              no_barcode: no_sn, // 필드명 바꾸기
              fg_unit: "SN", // LOT/SN구분
              no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              qt_isu: 1, // 적용량 추가
              no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = snData.filter(
              (newItem: any) =>
                !prevData.some(
                  (oldItem: any) =>
                    oldItem.cd_item === newItem.cd_item &&
                    oldItem.cd_wh === newItem.cd_wh
                )
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        break;
      case "btnApplyInv":
        {
          // 대체출고는 입/출고 창고를 고정하지 않으므로 창고는 넘기지 않는다(기본 공백).
          const selectedFormDataInv = searchRef.current.getFormdata();
          const selectedDataInv = {
            cd_biz: selectedFormDataInv.cd_biz,
          };

          // 이미 이 건에 들어와 있는 품목 · 창고.
          // grid2 는 grid1 의 no_io 로 필터되므로 필터된 행 = 지금 건의 라인이다.
          // 팝업에 넘겨 조회 결과에서 빼고(정상 경로), 아래 적용 루프에서 한 번 더 거른다
          // (+ 로 직접 추가한 라인과 겹치는 경우는 팝업이 모른다).
          const invKeys = new Set<string>();
          getGridCurrent("grid2").forEachFilteredNode((n: RowNode) => {
            invKeys.add(`${n.data?.cd_item}\u0000${n.data?.cd_wh}`);
          });

          const componentInv = (
            <SN_MTL_MTI_REG_002_POP_002
              selectedData={selectedDataInv}
              excludeKeys={invKeys}
            />
          );
          const resultInv = await showResizablePopupModal({
            title: "재고적용",
            children: componentInv,
          });

          if (!resultInv || resultInv === "close") return;
          const invResult = resultInv[0];

          const focusedRowNodeInvH = getGridCurrent("grid1").focusedRowNode();
          const lotItemListInv: any = [];
          const snItemListInv: any = [];

          let firstAddedNode: any = null;
          // 재고리스트가 채워진 뒤 자동으로 선입선출을 돌릴 LOT/SN 라인
          const pendingFifoInv: { node: any; qt: number }[] = [];

          const dupInv: string[] = [];

          invResult.map((row: any) => {
            const invKey = `${row.cd_item}\u0000${row.cd_wh}`;
            if (invKeys.has(invKey)) {
              dupInv.push(`${row.cd_item} / ${row.nm_wh || row.cd_wh}`);
              return;
            }
            invKeys.add(invKey); // 팝업 결과 안에 같은 조합이 둘 이상 있어도 한 번만 넣는다

            const addedNode = onAddRow();
            if (!addedNode) return;
            if (!firstAddedNode) firstAddedNode = addedNode;

            for (const key in row) {
              addedNode.setDataValue(key, row[key], "noEvent");
            }
            addedNode.setDataValue("nm_wh", row.nm_wh, "noEvent");
            addedNode.setDataValue("yn_req", "N", "noEvent");

            // 셋 다 현재고만큼 잡는다. LOT/SN 은 재고리스트를 받은 뒤 선입선출로(아래 pendingFifoInv).
            if (row.fg_unit === "QT") {
              addedNode.setDataValue("qt_good", row.qt_inv, "noEvent");
              addedNode.setDataValue("qt", row.qt_inv, "noEvent");
            } else {
              addedNode.setDataValue("qt_good", 0, "noEvent");
              addedNode.setDataValue("qt", 0, "noEvent");
              pendingFifoInv.push({
                node: addedNode,
                qt: Number(row.qt_inv) || 0,
              });
            }

            switch (row.fg_unit) {
              case "LOT":
                lotItemListInv.push({
                  cd_biz: focusedRowNodeInvH.data.cd_biz,
                  cd_item: row.cd_item,
                  cd_wh: row.cd_wh,
                  cd_loc: "",
                });
                break;
              case "SN":
                snItemListInv.push({
                  cd_biz: focusedRowNodeInvH.data.cd_biz,
                  cd_item: row.cd_item,
                  cd_wh: row.cd_wh,
                });
                break;
            }
            // 신규행 수량 수정 가능하도록 라인은 block 하지 않는다(dc_editcon의 new만 적용)
          });

          CalAmBulk(
            gridRefs.current["grid1"],
            gridRefs.current["grid2"],
            "rt_exch"
          );
          getGridCurrent("grid1").applyBlock(focusedRowNodeInvH);

          // 구분에 따라 재고리스트(grid3) 조회
          if (lotItemListInv.length > 0) {
            const searchLotData = await fnGetWhLocLotCtPltInv(lotItemListInv);
            const lotData = searchLotData.map((item: any) => {
              const { no_lot, ...rest } = item;
              return {
                ...rest,
                no_barcode: no_lot,
                fg_unit: "LOT",
                qt_isu: 0,
              };
            });

            setLotSnData((prevData: any[]) => {
              const newData = lotData.filter(
                (newItem: any) =>
                  !prevData.some(
                    (oldItem: any) =>
                      oldItem.cd_item === newItem.cd_item &&
                      oldItem.cd_wh === newItem.cd_wh
                  )
              );
              return newData.length === 0
                ? prevData
                : [...prevData, ...newData];
            });
          }
          if (snItemListInv.length > 0) {
            const searchSnData = await fnGetWhSnInv(snItemListInv);
            const snData = searchSnData.map((item: any) => {
              const { no_sn, ...rest } = item;
              return {
                ...rest,
                no_barcode: no_sn,
                fg_unit: "SN",
                no_src: item.no_io,
                no_line_src: item.no_line,
                qt_isu: 1,
                no_origin: item.no_origin,
                no_line_origin: item.no_line_origin,
              };
            });

            setLotSnData((prevData: any[]) => {
              const newData = snData.filter(
                (newItem: any) =>
                  !prevData.some(
                    (oldItem: any) =>
                      oldItem.cd_item === newItem.cd_item &&
                      oldItem.cd_wh === newItem.cd_wh
                  )
              );
              return newData.length === 0
                ? prevData
                : [...prevData, ...newData];
            });
          }

          // grid3 은 lotSnData(state)로 그려지므로 setLotSnData 직후에는 아직 행이 없다.
          // 이 라인의 재고가 grid3 에 들어올 때까지만 기다린다(필터 전 기준).
          const waitLotSnRow = async (node: any) => {
            for (let i = 0; i < 30; i++) {
              let found = false;
              getGridCurrent("grid3").forEachNode((n: RowNode) => {
                if (
                  n.data?.cd_item === node.data.cd_item &&
                  n.data?.cd_wh === node.data.cd_wh
                ) {
                  found = true;
                }
              });
              if (found) return true;
              await new Promise((resolve) =>
                requestAnimationFrame(() => resolve(null))
              );
            }
            return false;
          };

          // 재고수량만큼 못 채운 라인. LOT/시리얼 재고가 재고수량보다 적으면 생긴다.
          const shortInv: string[] = [];

          for (const pending of pendingFifoInv) {
            if (pending.qt <= 0) continue;
            if (!(await waitLotSnRow(pending.node))) {
              shortInv.push(
                `${pending.node.data.cd_item} / ${pending.node.data.nm_wh} : 0 / ${pending.qt}`
              );
              continue;
            }

            getGridCurrent("grid2").clearFocus();
            getGridCurrent("grid2").selectAndFocusRow(
              pending.node.rowIndex ?? 0
            );

            // 포커스로 걸리는 하위 필터는 setFocusedCell(rAF) → handleCellFocused 를 거쳐
            // 다음 프레임에야 적용된다. selectInv 는 grid3 의 '필터된 행'을 훑으므로
            // 그때까지 기다리지 않고 onFocusedRowChangedL 과 같은 조건으로 여기서 직접 건다.
            getGridCurrent("grid3").applyFilters({
              cd_item: pending.node.data.cd_item,
              cd_wh: pending.node.data.cd_wh,
              qt_inv: "notZero",
            });

            selectInv("F", "", pending.qt);

            const qtDone = Number(pending.node.data?.qt_good) || 0;
            if (qtDone < pending.qt) {
              shortInv.push(
                `${pending.node.data.cd_item} / ${pending.node.data.nm_wh} : ${qtDone} / ${pending.qt}`
              );
            }
          }

          // 건너뛰거나 덜 채운 건은 조용히 두면 사용자가 저장할 때까지 모른다.
          const noticeInv: string[] = [];
          if (dupInv.length > 0) {
            noticeInv.push(
              "아래 품목은 이미 등록되어 있어 건너뛰었습니다.\n" +
                dupInv.join("\n")
            );
          }
          if (shortInv.length > 0) {
            noticeInv.push(
              "아래 품목은 LOT/시리얼 재고가 재고수량보다 적어 일부만 적용했습니다.\n" +
                "재고리스트에서 직접 확인해 주세요. (적용 / 재고수량)\n" +
                shortInv.join("\n")
            );
          }
          if (noticeInv.length > 0) {
            await showDialog("alter", noticeInv.join("\n\n"));
          }

          // 적용한 첫 라인을 포커스해 해당 건의 재고리스트가 바로 보이게 한다
          if (firstAddedNode != null) {
            getGridCurrent("grid2").clearFocus();
            getGridCurrent("grid2").selectAndFocusRow(
              firstAddedNode.rowIndex ?? 0
            );
          }
        }
        break;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedRowNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      let cd_wh_last = "";
      let nm_wh_last = "";
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount !== 0) {
        const lastNode = getGridCurrent("grid2").getRowNodeAtIndex(
          rowCount - 1
        );

        if (lastNode != null) {
          cd_wh_last = lastNode.data.cd_wh;
          nm_wh_last = lastNode.data.nm_wh;
        }
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_map: focusedRowNodeH.data?.fg_map,
        fg_vat: "11",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        qt_good: 0,
        qt_bad: 0,
        cd_wh: cd_wh_last,
        nm_wh: nm_wh_last,
        yn_req: "N",
        qt_req: 0,
        cd_pjt: focusedRowNodeH.data?.cd_pjt,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "cd_wh":
        const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
        if (rowCount === 1) return;
        if (!rowData.cd_wh) return;

        let apply = false;
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        });
        if (!apply) return;

        const confirm = await showDialog(
          "confirm",
          "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
        );

        if (confirm) {
          const visibleIdx = rowNode.rowIndex;

          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          });
          if (visibleIdx) {
            getGridCurrent("grid2").selectAndFocusRow(visibleIdx);
          }
        }
        break;
      // 품목바뀌면 수량 초기화
      case "cd_item":
        rowData.qt = 0;
        rowData.qt_good = 0;
        rowData.qt_bad = 0;
        break;
      case "qt_good":
      case "qt_bad":
      case "um_k":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad, "noEvent");
        // 계산용 내부 데이터 업데이트
        // rowNode.setDataValue("um", rowData.um_k);
        // rowNode.setDataValue("am", rowData.am_k);
        rowData.um = rowData.um_k;
        rowData.am = rowData.am_k;

        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
    }

    switch (colId) {
      case "cd_item":
      case "cd_wh":
      case "nm_wh":
        if (
          rowData.cd_item !== "" &&
          rowData.cd_item !== null &&
          rowData.cd_item !== undefined &&
          rowData.cd_wh !== "" &&
          rowData.cd_wh !== null &&
          rowData.cd_wh !== undefined
        ) {
          // 품목이나 창고가 바뀌면 꼬일 수 있으므로 초기화
          rowData.qt = 0;
          rowData.qt_good = 0;
          rowData.qt_bad = 0;
          resetRowData();

          const data = {
            cd_biz: rowData.cd_biz,
            cd_item: rowData.cd_item,
            cd_wh: rowData.cd_wh,
            cd_loc: "",
          };

          if (
            !lotSnDataRef.current.some(
              (item) =>
                item.cd_item === rowData.cd_item && item.cd_wh === rowData.cd_wh
            )
          ) {
            switch (rowData.fg_unit) {
              case "LOT":
                const searchLotData = await fnGetWhLocLotCtPltInv(data);
                const lotData = searchLotData.map((item: any) => {
                  const { no_lot, ...rest } = item; // no_lot만 추출

                  return {
                    ...rest,
                    no_barcode: no_lot, // 필드명 바꾸기
                    fg_unit: "LOT", // LOT/SN구분
                    qt_isu: 0, // 적용량 추가
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...lotData];
                });
                break;
              case "SN":
                const searchSnData = await fnGetWhSnInv(data);
                const snData = searchSnData.map((item: any) => {
                  const { no_sn, ...rest } = item;

                  return {
                    ...rest,
                    no_barcode: no_sn, // 필드명 바꾸기
                    fg_unit: "SN", // LOT/SN구분
                    no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                    qt_isu: 1, // 적용량 추가
                    no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                    no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  };
                });

                // 이전 데이터에 새로 조회한 데이터 합치기
                setLotSnData((prevData) => {
                  // 이미 해당 품목+창고 데이터가 prevData에 있는지 다시 한번 확인 (상태 업데이트 시점의 최신값 기준)
                  const isAlreadyExist = prevData.some(
                    (item) =>
                      item.cd_item === rowData.cd_item &&
                      item.cd_wh === rowData.cd_wh
                  );

                  if (isAlreadyExist) return prevData; // 있으면 추가 안 함
                  return [...prevData, ...snData];
                });
                break;
            }
          }
          // 재고리스트 필터돌게
          getGridCurrent("grid2").clearFocus();
          getGridCurrent("grid2").selectAndFocusRow(rowNode.rowIndex);
        } else {
          resetRowData();
        }
        break;
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      if (selectedRow.state === "insert") {
        getGridCurrent("grid3").applyFilters({
          cd_item: selectedRow.cd_item,
          cd_wh: selectedRow.cd_wh,
          qt_inv: "notZero",
        });
        const qtRemain = selectedRow.qt_req - selectedRow.qt;
        if (qtRemain > 0 && selectedRow.fg_unit !== "QT") {
          numberBoxFifoRef.current.setValue(qtRemain);
        } else {
          numberBoxFifoRef.current.setValue(0);
        }
      } else {
        getGridCurrent("grid3").applyFilters({
          cd_item: "",
          cd_wh: "",
        });
      }
    } else {
      getGridCurrent("grid3").applyFilters({
        cd_item: "",
        cd_wh: "",
      });
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        onAddRow();
        break;
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region GridLotSn Events
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    // 신규일 때만 바코드 컴포넌트 포커스 타게
    if (focusedRowNodeL) {
      if (focusedRowNodeL.data.state === "insert") {
        txtBarcodeRef.current.focus();
      }
    }
  };

  const onModelUpdatedLotSn = () => {
    // 적용팝업 푸터갱신
    getGridCurrent("grid3").refreshFooter();
  };
  //#endregion

  //#region GridD Events
  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        if (focusedRowNodeL.data.state !== "insert") {
          await showDialog("alter", "신규건만 삭제할 수 있습니다.");
          return;
        }

        const focusedRowNodeD = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_item === focusedRowNodeD.data?.cd_item &&
            node.data.cd_wh === focusedRowNodeD.data?.cd_wh &&
            node.data.no_barcode === focusedRowNodeD.data?.no_barcode &&
            node.data.dt_io === focusedRowNodeD.data?.dt_io_origin
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedRowNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_good",
              focusedRowNodeL.data?.qt_good - focusedRowNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeL.data.cd_wh,
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Fifo":
        selectInv("F", "");
    }
  };
  //#endregion

  //#region Method
  // qtFifo : 선입선출 수량을 밖에서 넘길 때 쓴다(재고적용 자동 배분).
  // SnNumberBox.getValue 는 setValue 직후 바로 안 바뀌므로(React state) 입력칸을 거치면 안 된다.
  const selectInv = (id: string, barcode: string, qtFifo?: number) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        const qtFifoUse =
          qtFifo != null ? qtFifo : Number(numberBoxFifoRef.current.getValue);
        if (!qtFifoUse) {
          showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = qtFifoUse;
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        // 요청량 있으면 요청량이 잔량
        if (focusedRowNodeL.data.qt_req !== 0) {
          qtRemain = focusedRowNodeL.data.qt_req - focusedRowNodeL.data.qt;

          if (qtRemain === 0) {
            showDialog("alter", "요청수량이 다 등록되었습니다.");
            return false;
          }
        }
        // 요청량이 없으면 바코드 전체 선택
        else {
          qtRemain = -1;
        }
        break;
    }

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_io: focusedRowNodeL.data?.no_io,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeL.data?.cd_wh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_batch: node.data.no_batch, // 26.11.02 추가
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_item &&
          node4.data.cd_wh === focusedRowNodeL.data?.cd_wh &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: focusedRowNodeL.data.cd_wh,
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node4.data.cd_item === node.data?.cd_item &&
          node4.data.cd_wh === node.data?.cd_wh &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue("qt_inv", node.data.qt_inv + node4.data.qt_good);

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      cd_item: "",
      cd_wh: "",
    });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onBtnAddClick: onGridLBtnsClick,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
          onModelUpdated: onModelUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "출고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"재고적용"}
                  onClick={() => {
                    onGridHBtnsClick("btnApplyInv");
                  }}
                />
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid4"]],
                  columns: ["no_io", "no_line"],
                }}
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={lotSnData} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "재고리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div>
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          inputRef={txtBarcodeRef}
                        />
                      </div>
                      <div className="w-[80px]">
                        <SnNumberBox
                          inputRef={numberBoxFifoRef}
                          customFormat="QT"
                        />
                      </div>
                      <div>
                        <SnButton
                          text="선입선출"
                          onClick={() => {
                            onSnButtonClick("Fifo");
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single", title: "출고정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
        <div style={{ display: "none" }}>
          <SnGrid
            ref={sheetref1} // Grid Name
            columnConfig={grids.sheet1.columns} // 컬럼 설정 전달
            rowData={grids.sheet1.dataSource} // Datasource
          />
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MTI_REG_002;
